/* eslint-disable import/no-duplicates */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Center,
  Image,
  Input,
  FormControl,
  FormLabel,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  RiCheckLine,
  RiCloseLine,
  RiShieldCheckLine,
  RiEditLine,
} from 'react-icons/ri';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { formatReportDate } from '../../utils/formatReportDate';

import {
  IExpenseReport,
  SignatureStyle,
  isFixedRateKmObservation,
} from '../Dashboard/vehicles_management/Expenses';
import { apllyToast } from '../../components/Global/Toast2.0';
import { api } from '../../services/api';
import logo from '../../assets/logo2026.png';

// Configuração gov.br - Assinatura Eletrônica Avançada
const GOV_BR_CONFIG = {
  authUrl: 'https://sso.staging.acesso.gov.br/authorize',
  clientId: '', // Preencher após registro no Login Único gov.br
};

const SIGNATURE_STYLES: {
  value: SignatureStyle;
  label: string;
  font: string;
}[] = [
  { value: 'cursive-1', label: 'Cursiva 1', font: 'Dancing Script, cursive' },
  { value: 'cursive-2', label: 'Cursiva 2', font: 'Pacifico, cursive' },
  { value: 'cursive-3', label: 'Cursiva 3', font: 'Great Vibes, cursive' },
  { value: 'elegant-1', label: 'Elegante 1', font: 'Playfair Display, serif' },
  {
    value: 'elegant-2',
    label: 'Elegante 2',
    font: 'Cormorant Garamond, serif',
  },
];

interface SignExpenseParams {
  reportId: string;
}

export default function SignExpense() {
  const { reportId } = useParams<SignExpenseParams>();
  const location = useLocation();

  const [report, setReport] = useState<IExpenseReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureStep, setSignatureStep] = useState<
    'idle' | 'confirm' | 'processing' | 'done'
  >('idle');

  // Campos do formulário de assinatura
  const [signerName, setSignerName] = useState('');
  const [signerCpf, setSignerCpf] = useState('');
  const [signerStyle, setSignerStyle] = useState<SignatureStyle>('cursive-1');

  // Detectar o papel do signatário:
  //  - ?role=manager        => gestor
  //  - ?participant=<id>     => participante vinculado
  //  - (nenhum)             => responsável
  const searchParams = new URLSearchParams(location.search);
  const isManagerRole = searchParams.get('role') === 'manager';
  const participantId = searchParams.get('participant');
  const isParticipantRole = !!participantId;

  const currentParticipant =
    isParticipantRole && report?.participants
      ? report.participants.find(p => p.id === participantId)
      : undefined;

  const isGovBrAvailable = !!GOV_BR_CONFIG.clientId;
  const redirectUri = `${window.location.origin}/sign-expense/${reportId}${
    isManagerRole ? '?role=manager' : ''
  }`;

  // Verificar se voltou do OAuth gov.br com authorization code
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');

    if (code && reportId) {
      handleGovBrCallback(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Carregar relatório
  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${api.defaults.baseURL}/expense-reports/${reportId}/public`,
        );

        if (!response.ok) {
          throw new Error('Relatório não encontrado');
        }

        const data = await response.json();

        if (data.items) {
          data.items = data.items.map((item: any) => ({
            ...item,
            amount: item.amount != null ? Number(item.amount) : undefined,
            additional_km:
              item.additional_km != null
                ? Number(item.additional_km)
                : undefined,
            km_start: item.km_start != null ? Number(item.km_start) : undefined,
            km_end: item.km_end != null ? Number(item.km_end) : undefined,
          }));
        }

        setReport(data);
      } catch (error) {
        console.error('Erro ao carregar relatório:', error);
        apllyToast('error', 'Erro ao carregar relatório. Verifique o link.');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // O valor de cada item já vem correto na origem (ModalAddNewExpense /
  // ModalEditExpense / ModalConsolidateExpenses aplicam a regra de faixa por
  // KM antes de gravar `amount`), então aqui é só usar o valor salvo.
  const getItemValue = (item: any): number => Number(item.amount) || 0;

  const getTotalValue = () => {
    if (!report) return 0;
    return report.items.reduce((sum, item) => sum + getItemValue(item), 0);
  };

  const getExpenseColor = (type: string): string => {
    if (type === 'Refeição') return 'green';
    if (type === 'Quilometragem adicional') return 'blue';
    if (type === 'Pedágio adicional') return 'orange';
    return 'purple';
  };

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9)
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
      6,
      9,
    )}-${digits.slice(9)}`;
  };

  // Abrir formulário de confirmação
  const handleSignClick = useCallback(() => {
    // Pre-preencher com nome do signatário conforme o papel
    let defaultName = report?.user?.name || report?.director_name || '';
    if (isManagerRole) {
      defaultName = report?.manager?.name || '';
    } else if (isParticipantRole) {
      defaultName =
        currentParticipant?.user?.name || currentParticipant?.name || '';
    }
    setSignerName(defaultName);
    setSignerCpf('');
    setSignerStyle('cursive-1');
    setSignatureStep('confirm');
  }, [report, isManagerRole, isParticipantRole, currentParticipant]);

  // Confirmar e assinar (assinatura segura via backend)
  const handleConfirmSign = async () => {
    if (!signerName.trim()) {
      apllyToast('error', 'Informe seu nome completo');
      return;
    }
    if (signerCpf.replace(/\D/g, '').length !== 11) {
      apllyToast('error', 'Informe um CPF válido com 11 dígitos');
      return;
    }

    setSigning(true);
    setSignatureStep('processing');

    try {
      // Endpoint conforme o papel: gestor, participante vinculado ou responsável
      let signEndpoint = `${api.defaults.baseURL}/expense-reports/${reportId}/sign`;
      if (isManagerRole) {
        signEndpoint = `${api.defaults.baseURL}/expense-reports/${reportId}/sign-manager`;
      } else if (isParticipantRole) {
        signEndpoint = `${api.defaults.baseURL}/expense-reports/${reportId}/sign-participant/${participantId}`;
      }

      const response = await fetch(signEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: signerName,
          style: signerStyle,
          cpf: signerCpf.replace(/\D/g, ''),
          certificate: `Assinatura eletrônica - ${signerName} - ${new Date().toISOString()}`,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao assinar relatório');
      }

      setSignatureStep('done');
      const successMsg = isManagerRole
        ? 'Relatório aprovado e assinado pelo gestor!'
        : 'Relatório assinado com sucesso!';
      apllyToast('success', successMsg);

      const updatedResponse = await fetch(
        `${api.defaults.baseURL}/expense-reports/${reportId}/public`,
      );
      if (updatedResponse.ok) {
        setReport(await updatedResponse.json());
      }
    } catch (error: any) {
      console.error('Erro ao assinar:', error);
      apllyToast('error', error.message || 'Erro ao assinar relatório.');
      setSignatureStep('idle');
    } finally {
      setSigning(false);
    }
  };

  // Assinar com gov.br (OAuth redirect)
  const handleSignWithGovBr = useCallback(() => {
    const authParams = new URLSearchParams({
      response_type: 'code',
      client_id: GOV_BR_CONFIG.clientId,
      redirect_uri: redirectUri,
      scope: 'sign',
    });

    window.location.href = `${GOV_BR_CONFIG.authUrl}?${authParams.toString()}`;
  }, [redirectUri]);

  // Callback do OAuth gov.br
  const handleGovBrCallback = async (code: string) => {
    setSignatureStep('processing');
    setSigning(true);

    try {
      const response = await fetch(
        `${api.defaults.baseURL}/expense-reports/${reportId}/sign-govbr`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao assinar com gov.br');
      }

      setSignatureStep('done');
      apllyToast('success', 'Relatório assinado digitalmente com sucesso!');

      const updatedResponse = await fetch(
        `${api.defaults.baseURL}/expense-reports/${reportId}/public`,
      );
      if (updatedResponse.ok) {
        setReport(await updatedResponse.json());
      }
    } catch (error: any) {
      console.error('Erro ao processar assinatura gov.br:', error);
      apllyToast('error', error.message || 'Erro ao assinar com gov.br');
      setSignatureStep('idle');
    } finally {
      setSigning(false);
    }
  };

  const handleReject = useCallback(async () => {
    try {
      const rejectEndpoint = isManagerRole
        ? `${api.defaults.baseURL}/expense-reports/${reportId}/reject-manager`
        : `${api.defaults.baseURL}/expense-reports/${reportId}/reject`;

      const response = await fetch(rejectEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao rejeitar relatório');
      }

      apllyToast('info', 'Relatório rejeitado');
      setTimeout(() => {
        window.close();
      }, 1000);
    } catch (error: any) {
      console.error('Erro ao rejeitar:', error);
      apllyToast('error', error.message || 'Erro ao rejeitar relatório');
    }
  }, [reportId, isManagerRole]);

  // === Estados de loading/erro/assinado ===

  if (loading) {
    return (
      <Center h="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.600" thickness="3px" />
          <Text color="#475569">Carregando relatório...</Text>
        </VStack>
      </Center>
    );
  }

  if (!report) {
    return (
      <Center h="100vh" bg="gray.50">
        <Box bg="white" p={8} borderRadius="lg" shadow="md" maxW="500px">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Relatório não encontrado</AlertTitle>
              <AlertDescription>
                O relatório solicitado não existe ou foi removido.
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      </Center>
    );
  }

  // Verificar se a assinatura relevante (usuário ou gestor) já foi feita
  const userIsSigned =
    report.signatureStatus === 'signed' || report.signature_status === 'signed';
  const managerIsSigned =
    report.managerSignatureStatus === 'signed' ||
    report.manager_signature_status === 'signed';

  const participantIsSigned = currentParticipant?.signature_status === 'signed';

  let alreadySigned = userIsSigned;
  if (isManagerRole) alreadySigned = managerIsSigned;
  else if (isParticipantRole) alreadySigned = participantIsSigned;

  // Quando um vinculado acessa o link, mostramos OS DADOS DELE (não os do
  // responsável): nome, setor/seção/centro de custo e dados bancários próprios.
  const displayName = isParticipantRole
    ? currentParticipant?.user?.name || currentParticipant?.name || ''
    : report.user?.name || report.director_name || report.director;
  const displaySector = isParticipantRole
    ? currentParticipant?.sector || report.sector
    : report.sector;
  const displaySection = isParticipantRole
    ? currentParticipant?.section || report.section
    : report.section;
  const displayCostCenter = isParticipantRole
    ? currentParticipant?.cost_center || report.cost_center
    : report.cost_center;
  const displayPixKey = isParticipantRole
    ? currentParticipant?.pix_key
    : report.pix_key || report.user?.pix_key;
  const displayBankName = isParticipantRole
    ? currentParticipant?.bank_name
    : report.bank_name || report.user?.bank_name;
  const displayBankAgency = isParticipantRole
    ? currentParticipant?.bank_agency
    : report.bank_agency || report.user?.bank_agency;
  const displayBankAccount = isParticipantRole
    ? currentParticipant?.bank_account
    : report.bank_account || report.user?.bank_account;

  // Participante anterior na ordem ainda não assinou
  const previousParticipantPending =
    isParticipantRole && currentParticipant
      ? (report.participants || []).find(
          p =>
            p.role === 'related' &&
            (p.signing_order || 0) < (currentParticipant.signing_order || 0) &&
            p.signature_status !== 'signed' &&
            p.signature_status !== 'rejected',
        )
      : undefined;

  // Link de participante inválido (id não encontrado no relatório)
  if (isParticipantRole && !currentParticipant) {
    return (
      <Center h="100vh" bg="#f8fafc" color="#1e293b">
        <Box bg="white" p={8} borderRadius="lg" shadow="md" maxW="500px">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Link inválido</AlertTitle>
              <AlertDescription>
                Este link de assinatura não corresponde a um participante deste
                relatório.
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      </Center>
    );
  }

  // Participante só pode assinar após o responsável e os anteriores da ordem
  if (
    isParticipantRole &&
    signatureStep !== 'done' &&
    !participantIsSigned &&
    (!userIsSigned || !!previousParticipantPending)
  ) {
    return (
      <Center h="100vh" bg="#f8fafc" color="#1e293b">
        <Box bg="white" p={8} borderRadius="lg" shadow="md" maxW="500px">
          <VStack spacing={4}>
            <Icon as={RiShieldCheckLine} fontSize="48" color="orange.400" />
            <Heading size="md" color="#1e293b">
              Aguardando assinatura anterior
            </Heading>
            <Text color="#475569" textAlign="center">
              {!userIsSigned
                ? 'O responsável precisa assinar o relatório antes de você.'
                : 'Um participante anterior na ordem de assinatura ainda não assinou. Você receberá o link assim que for a sua vez.'}
            </Text>
          </VStack>
        </Box>
      </Center>
    );
  }

  // Gestor só pode assinar após o responsável e todos os vinculados
  const anyRelatedPending = (report.participants || []).some(
    p =>
      p.role === 'related' &&
      p.signature_status !== 'signed' &&
      p.signature_status !== 'rejected',
  );

  if (
    isManagerRole &&
    (!userIsSigned || anyRelatedPending) &&
    signatureStep !== 'done'
  ) {
    return (
      <Center h="100vh" bg="#f8fafc" color="#1e293b">
        <Box bg="white" p={8} borderRadius="lg" shadow="md" maxW="500px">
          <VStack spacing={4}>
            <Icon as={RiShieldCheckLine} fontSize="48" color="orange.400" />
            <Heading size="md" color="#1e293b">
              Aguardando assinaturas
            </Heading>
            <Text color="#475569" textAlign="center">
              {!userIsSigned
                ? 'O relatório precisa ser assinado pelo responsável antes da aprovação do gestor.'
                : 'Todos os participantes vinculados precisam assinar antes da aprovação do gestor.'}
            </Text>
          </VStack>
        </Box>
      </Center>
    );
  }

  if (alreadySigned && signatureStep !== 'done') {
    let signedAt = report.signedAt || report.signed_at;
    if (isManagerRole) {
      signedAt = report.managerSignedAt || report.manager_signed_at;
    } else if (isParticipantRole) {
      signedAt = currentParticipant?.signed_at;
    }

    let statusMessage = 'Este relatório já foi assinado digitalmente';
    if (isManagerRole) {
      statusMessage = 'Este relatório já foi aprovado pelo gestor';
    } else if (isParticipantRole) {
      statusMessage = 'Você já assinou este relatório';
    }

    return (
      <Center h="100vh" bg="#f8fafc" color="#1e293b">
        <Box bg="white" p={8} borderRadius="lg" shadow="md" maxW="500px">
          <VStack spacing={4}>
            <Icon as={RiShieldCheckLine} fontSize="48" color="green.500" />
            <Heading size="md" color="#1e293b">
              {isManagerRole
                ? 'Relatório já aprovado'
                : 'Relatório já assinado'}
            </Heading>
            <Text color="#475569" textAlign="center">
              {statusMessage}
              {signedAt &&
                ` em ${format(
                  new Date(signedAt as string),
                  "dd/MM/yyyy 'às' HH:mm",
                  {
                    locale: ptBR,
                  },
                )}`}
            </Text>
          </VStack>
        </Box>
      </Center>
    );
  }

  if (signatureStep === 'done') {
    return (
      <Center h="100vh" bg="gray.50">
        <Box bg="white" p={8} borderRadius="lg" shadow="md" maxW="500px">
          <VStack spacing={4}>
            <Icon as={RiCheckLine} fontSize="48" color="green.500" />
            <Heading size="md" color="#1e293b">
              Assinatura realizada!
            </Heading>
            <Text color="#475569" textAlign="center">
              O relatório foi assinado com sucesso. Você pode fechar esta
              página.
            </Text>
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={() => window.close()}
            >
              Fechar página
            </Button>
          </VStack>
        </Box>
      </Center>
    );
  }

  // === Página principal ===

  return (
    <Box minH="100vh" bg="#f8fafc" py={8} color="#1e293b">
      <Box maxW="900px" mx="auto" px={6}>
        {/* Header */}
        <VStack spacing={4} mb={6} align="stretch">
          <HStack spacing={4} align="center">
            <Image src={logo} alt="Logo" maxH="40px" fallback={<Box />} />
            <Divider orientation="vertical" h="30px" borderColor="#cbd5e1" />
            <VStack align="start" spacing={0}>
              <Heading size="md" color="#1e293b">
                {isManagerRole
                  ? 'Aprovação de Relatório'
                  : 'Assinatura de Relatório'}
              </Heading>
              <Text fontSize="sm" color="#64748b">
                Relatório de Despesas e Reembolsos - COOASGO
              </Text>
            </VStack>
          </HStack>
        </VStack>

        {/* Conteúdo do Relatório */}
        <Box
          bg="white"
          borderRadius="lg"
          shadow="sm"
          border="1px solid"
          borderColor="#e2e8f0"
        >
          <VStack spacing={0} align="stretch">
            {/* Informações do Relatório */}
            <Box p={6} borderBottom="1px solid" borderColor="#e2e8f0">
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="#64748b"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={4}
              >
                Informações do Relatório
              </Text>
              <HStack spacing={8} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="#64748b">
                    Usuário
                  </Text>
                  <Text fontWeight="bold" color="#1e293b">
                    {displayName}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="#64748b">
                    Data do gasto
                  </Text>
                  <Text color="#334155">
                    {formatReportDate(report.date, 'dd/MM/yyyy')}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="#64748b">
                    Setor / Seção
                  </Text>
                  <Text color="#334155">
                    {displaySector} / {displaySection}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="#64748b">
                    Centro de Custo
                  </Text>
                  <Text color="#334155">{displayCostCenter}</Text>
                </Box>
                {report.branch && (
                  <Box>
                    <Text fontSize="xs" color="#64748b">
                      Filial
                    </Text>
                    <Text color="#334155">{report.branch.short_name}</Text>
                  </Box>
                )}
              </HStack>
            </Box>

            {/* Dados Bancários (do participante/responsável que está assinando) */}
            {(displayPixKey || displayBankName) && (
              <Box
                p={6}
                borderBottom="1px solid"
                borderColor="#e2e8f0"
                bg="#f0f9ff"
              >
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#0284c7"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={3}
                >
                  Dados Bancários para Ressarcimento
                </Text>
                <HStack spacing={8} flexWrap="wrap">
                  {displayPixKey && (
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Chave PIX
                      </Text>
                      <Text fontWeight="medium" color="#1e293b">
                        {displayPixKey}
                      </Text>
                    </Box>
                  )}
                  {displayBankName && (
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Banco
                      </Text>
                      <Text color="#334155">{displayBankName}</Text>
                    </Box>
                  )}
                  {displayBankAgency && (
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Agência
                      </Text>
                      <Text color="#334155">{displayBankAgency}</Text>
                    </Box>
                  )}
                  {displayBankAccount && (
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Conta
                      </Text>
                      <Text color="#334155">{displayBankAccount}</Text>
                    </Box>
                  )}
                </HStack>
              </Box>
            )}

            {/* Despesas */}
            <Box p={6} borderBottom="1px solid" borderColor="#e2e8f0">
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="#64748b"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={4}
              >
                Despesas ({report.items.length}{' '}
                {report.items.length === 1 ? 'item' : 'itens'})
              </Text>

              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color="#475569" borderColor="#e2e8f0">
                      Tipo
                    </Th>
                    <Th color="#475569" borderColor="#e2e8f0">
                      Detalhes
                    </Th>
                    <Th color="#475569" borderColor="#e2e8f0" isNumeric>
                      Valor
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {report.items.map(item => {
                    const receipt =
                      item.receipt && !(item.receipt instanceof File)
                        ? item.receipt
                        : null;
                    const receiptUrl = receipt?.url || null;
                    const receiptName =
                      receipt?.original_name || receipt?.name || null;
                    const itemDate = (item as any).created_at;

                    return (
                      <Tr key={item.id}>
                        <Td borderColor="#f1f5f9">
                          <VStack align="start" spacing={1}>
                            <Badge
                              colorScheme={getExpenseColor(item.expense_type)}
                            >
                              {item.expense_type}
                            </Badge>
                            {itemDate && (
                              <Text fontSize="2xs" color="#94a3b8">
                                lançado em:{' '}
                                {format(
                                  new Date(itemDate),
                                  "dd/MM/yyyy 'às' HH:mm",
                                  {
                                    locale: ptBR,
                                  },
                                )}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td borderColor="#f1f5f9">
                          {item.expense_type === 'Quilometragem adicional' ? (
                            (() => {
                              const isFixedRate = isFixedRateKmObservation(
                                item.observations,
                              );
                              let fixedRate = 0;
                              let obsTexto = '';
                              try {
                                if (item.observations) {
                                  const parsed = JSON.parse(item.observations);
                                  if (parsed.km_rate)
                                    fixedRate = parsed.km_rate;
                                  if (parsed.obs) obsTexto = parsed.obs;
                                }
                              } catch (e) {
                                obsTexto = item.observations || '';
                              }
                              const kmStartR = (item as any).receipts?.find(
                                (r: any) => r.receipt_type === 'km_start',
                              );
                              const kmEndR = (item as any).receipts?.find(
                                (r: any) => r.receipt_type === 'km_end',
                              );

                              return (
                                <VStack align="start" spacing={2}>
                                  <HStack spacing={3}>
                                    <Text
                                      fontSize="sm"
                                      fontWeight="bold"
                                      color="#1e293b"
                                    >
                                      {item.additional_km} km
                                    </Text>
                                    {isFixedRate && (
                                      <Text fontSize="xs" color="#3b82f6">
                                        × R$ {fixedRate.toFixed(2)}
                                      </Text>
                                    )}
                                  </HStack>

                                  {(item.km_start != null ||
                                    item.km_end != null) && (
                                    <HStack
                                      spacing={4}
                                      bg="#f1f5f9"
                                      px={3}
                                      py={2}
                                      borderRadius="md"
                                      fontSize="xs"
                                    >
                                      <Box>
                                        <Text color="#64748b">Saída</Text>
                                        <Text
                                          color="#1e293b"
                                          fontWeight="medium"
                                        >
                                          {item.km_start?.toLocaleString(
                                            'pt-BR',
                                          )}{' '}
                                          km
                                        </Text>
                                      </Box>
                                      <Text color="#94a3b8" fontSize="md">
                                        →
                                      </Text>
                                      <Box>
                                        <Text color="#64748b">Chegada</Text>
                                        <Text
                                          color="#1e293b"
                                          fontWeight="medium"
                                        >
                                          {item.km_end?.toLocaleString('pt-BR')}{' '}
                                          km
                                        </Text>
                                      </Box>
                                    </HStack>
                                  )}

                                  {(kmStartR || kmEndR) && (
                                    <HStack spacing={2}>
                                      {kmStartR && (
                                        <HStack
                                          as="a"
                                          href={kmStartR.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          spacing={1}
                                          bg="#f1f5f9"
                                          px={2}
                                          py={1}
                                          borderRadius="sm"
                                          _hover={{ bg: '#e2e8f0' }}
                                        >
                                          <Text fontSize="2xs" color="#2563eb">
                                            📷 Hodômetro Saída
                                          </Text>
                                        </HStack>
                                      )}
                                      {kmEndR && (
                                        <HStack
                                          as="a"
                                          href={kmEndR.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          spacing={1}
                                          bg="#f1f5f9"
                                          px={2}
                                          py={1}
                                          borderRadius="sm"
                                          _hover={{ bg: '#e2e8f0' }}
                                        >
                                          <Text fontSize="2xs" color="#2563eb">
                                            📷 Hodômetro Chegada
                                          </Text>
                                        </HStack>
                                      )}
                                    </HStack>
                                  )}

                                  {item.displacement_reason && (
                                    <Text
                                      fontSize="xs"
                                      color="#64748b"
                                      wordBreak="break-word"
                                      whiteSpace="pre-wrap"
                                    >
                                      Motivo: {item.displacement_reason}
                                    </Text>
                                  )}
                                  {obsTexto && (
                                    <Text
                                      fontSize="xs"
                                      color="#64748b"
                                      wordBreak="break-word"
                                      whiteSpace="pre-wrap"
                                    >
                                      Obs: {obsTexto}
                                    </Text>
                                  )}
                                </VStack>
                              );
                            })()
                          ) : (
                            <VStack align="start" spacing={1}>
                              {item.observations && (
                                <Box fontSize="xs" color="#64748b">
                                  {(() => {
                                    if (
                                      item.expense_type === 'Ajuda de custo'
                                    ) {
                                      try {
                                        const parsed = JSON.parse(
                                          item.observations,
                                        );
                                        return (
                                          <VStack
                                            align="start"
                                            spacing={1}
                                            mt={1}
                                          >
                                            <Text
                                              fontWeight="bold"
                                              color="#1a5490"
                                              fontSize="xs"
                                            >
                                              DADOS DO EVENTO
                                            </Text>
                                            {parsed.nome && (
                                              <Text>Nome: {parsed.nome}</Text>
                                            )}
                                            {parsed.objetivo && (
                                              <Text>
                                                Objetivo: {parsed.objetivo}
                                              </Text>
                                            )}
                                            {parsed.local_evento && (
                                              <Text>
                                                Local: {parsed.local_evento}
                                              </Text>
                                            )}
                                            {parsed.endereco && (
                                              <Text>
                                                Endereço: {parsed.endereco}
                                              </Text>
                                            )}
                                            {parsed.periodo_evento && (
                                              <Text>
                                                Período: {parsed.periodo_evento}
                                              </Text>
                                            )}
                                            {parsed.horarios?.map(
                                              (h: any, i: number) => (
                                                /* eslint-disable-next-line react/no-array-index-key */
                                                <Text key={i}>
                                                  Horário ({i + 1}): Início:{' '}
                                                  {h.inicio || '-'} / Término:{' '}
                                                  {h.termino || '-'}
                                                </Text>
                                              ),
                                            )}
                                            <Text
                                              fontWeight="bold"
                                              color="#1a5490"
                                              fontSize="xs"
                                              mt={2}
                                            >
                                              DADOS DA VIAGEM
                                            </Text>
                                            {parsed.dados_viagem_data && (
                                              <Text>
                                                Data: {parsed.dados_viagem_data}
                                              </Text>
                                            )}
                                            {parsed.trecho_ida && (
                                              <Text>
                                                Ida: {parsed.trecho_ida}{' '}
                                                {parsed.trecho_ida_data
                                                  ? `(em ${parsed.trecho_ida_data})`
                                                  : ''}
                                              </Text>
                                            )}
                                            {parsed.trecho_volta && (
                                              <Text>
                                                Volta: {parsed.trecho_volta}{' '}
                                                {parsed.trecho_volta_data
                                                  ? `(em ${parsed.trecho_volta_data})`
                                                  : ''}
                                              </Text>
                                            )}
                                          </VStack>
                                        );
                                      } catch (e) {
                                        return item.observations;
                                      }
                                    }
                                    return item.observations;
                                  })()}
                                </Box>
                              )}
                              {receiptUrl && (
                                <HStack
                                  as="a"
                                  href={receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  spacing={1}
                                  bg="#f1f5f9"
                                  px={2}
                                  py={1}
                                  borderRadius="sm"
                                  _hover={{ bg: '#e2e8f0' }}
                                  cursor="pointer"
                                >
                                  <Text fontSize="xs" color="#2563eb">
                                    {receiptName || 'Ver comprovante'}
                                  </Text>
                                </HStack>
                              )}
                              {!receiptUrl && receipt && (
                                <Text fontSize="xs" color="#94a3b8">
                                  Comprovante anexado
                                </Text>
                              )}
                            </VStack>
                          )}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight="medium"
                          borderColor="#f1f5f9"
                          color="#1e293b"
                        >
                          {item.expense_type !== 'Quilometragem adicional' ||
                          isFixedRateKmObservation(item.observations)
                            ? formatCurrency(getItemValue(item))
                            : '—'}
                        </Td>
                      </Tr>
                    );
                  })}
                  <Tr bg="#eff6ff">
                    <Td
                      colSpan={2}
                      fontWeight="bold"
                      borderColor="#bfdbfe"
                      color="#1e293b"
                    >
                      VALOR TOTAL
                    </Td>
                    <Td
                      isNumeric
                      fontWeight="bold"
                      fontSize="lg"
                      color="#1d4ed8"
                      borderColor="#bfdbfe"
                    >
                      {formatCurrency(getTotalValue())}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            {/* Ações */}
            <Box p={6}>
              <Alert
                status="warning"
                fontSize="sm"
                borderRadius="md"
                mb={6}
                bg="#fffbeb"
                border="1px solid"
                borderColor="#fde68a"
              >
                <AlertIcon color="#f59e0b" />
                <Text color="#92400e">
                  Ao assinar, você confirma que todas as informações deste
                  relatório estão corretas e autoriza o ressarcimento no valor
                  total apresentado.
                </Text>
              </Alert>

              {/* Formulário de confirmação */}
              {signatureStep === 'confirm' && (
                <Box
                  bg="#f8fafc"
                  p={6}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="#e2e8f0"
                  mb={4}
                >
                  <Text fontWeight="bold" color="#1e293b" mb={4}>
                    Confirme seus dados para assinar
                  </Text>

                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl isRequired flex={2}>
                        <FormLabel fontSize="sm" color="#475569">
                          Nome completo
                        </FormLabel>
                        <Input
                          value={signerName}
                          onChange={e => setSignerName(e.target.value)}
                          placeholder="Seu nome completo"
                          bg="white"
                          color="#1e293b"
                        />
                      </FormControl>

                      <FormControl isRequired flex={1}>
                        <FormLabel fontSize="sm" color="#475569">
                          CPF
                        </FormLabel>
                        <Input
                          value={signerCpf}
                          onChange={e => {
                            setSignerCpf(formatCpf(e.target.value));
                          }}
                          placeholder="000.000.000-00"
                          bg="white"
                          color="#1e293b"
                          maxLength={14}
                        />
                      </FormControl>
                    </HStack>

                    {/* Estilo de assinatura */}
                    <FormControl>
                      <FormLabel fontSize="sm" color="#475569">
                        Estilo da assinatura
                      </FormLabel>
                      <SimpleGrid columns={[2, 3, 5]} spacing={2}>
                        {SIGNATURE_STYLES.map(style => (
                          <Box
                            key={style.value}
                            p={3}
                            borderRadius="md"
                            border="2px solid"
                            borderColor={
                              signerStyle === style.value
                                ? 'blue.500'
                                : '#e2e8f0'
                            }
                            bg={
                              signerStyle === style.value ? 'blue.50' : 'white'
                            }
                            cursor="pointer"
                            textAlign="center"
                            onClick={() => setSignerStyle(style.value)}
                            _hover={{ borderColor: 'blue.300' }}
                          >
                            <Text
                              fontFamily={style.font}
                              fontSize="lg"
                              color="#334155"
                              noOfLines={1}
                            >
                              {signerName || 'Nome'}
                            </Text>
                            <Text fontSize="xs" color="#64748b" mt={1}>
                              {style.label}
                            </Text>
                          </Box>
                        ))}
                      </SimpleGrid>
                    </FormControl>

                    {/* Preview */}
                    {signerName && (
                      <Box
                        bg="white"
                        p={4}
                        borderRadius="md"
                        border="1px dashed"
                        borderColor="#cbd5e1"
                        textAlign="center"
                      >
                        <Text fontSize="xs" color="#64748b" mb={2}>
                          Pré-visualização da assinatura
                        </Text>
                        <Text
                          fontFamily={
                            SIGNATURE_STYLES.find(s => s.value === signerStyle)
                              ?.font
                          }
                          fontSize="2xl"
                          color="blue.700"
                        >
                          {signerName}
                        </Text>
                        {signerCpf && (
                          <Text fontSize="xs" color="#64748b" mt={1}>
                            CPF: {signerCpf}
                          </Text>
                        )}
                      </Box>
                    )}

                    <HStack spacing={4} justify="flex-end" pt={2}>
                      <Button
                        variant="ghost"
                        color="#475569"
                        onClick={() => setSignatureStep('idle')}
                      >
                        Cancelar
                      </Button>
                      <Button
                        colorScheme="blue"
                        onClick={handleConfirmSign}
                        isLoading={signing}
                        loadingText={
                          isManagerRole ? 'Aprovando...' : 'Assinando...'
                        }
                        leftIcon={<Icon as={RiCheckLine} />}
                      >
                        {isManagerRole
                          ? 'Confirmar e Aprovar'
                          : 'Confirmar e Assinar'}
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* Botões principais */}
              {signatureStep === 'idle' && (
                <HStack spacing={4} justify="flex-end">
                  <Button
                    size="lg"
                    variant="outline"
                    colorScheme="red"
                    onClick={handleReject}
                    leftIcon={<Icon as={RiCloseLine} />}
                  >
                    Rejeitar
                  </Button>

                  {isGovBrAvailable ? (
                    <Button
                      size="lg"
                      colorScheme="blue"
                      onClick={handleSignWithGovBr}
                      leftIcon={<Icon as={RiShieldCheckLine} />}
                    >
                      Assinar com gov.br
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      colorScheme="blue"
                      onClick={handleSignClick}
                      leftIcon={<Icon as={RiEditLine} />}
                    >
                      {isManagerRole
                        ? 'Aprovar Relatório'
                        : 'Assinar Relatório'}
                    </Button>
                  )}
                </HStack>
              )}
            </Box>
          </VStack>
        </Box>

        {/* Footer */}
        <Text fontSize="xs" color="#94a3b8" textAlign="center" mt={6}>
          COOASGO - Cooperativa de Trabalho | Assinatura Eletrônica
        </Text>
      </Box>
    </Box>
  );
}
